import React, { useCallback, useRef, useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { List, ListItem, ListItemButton, ListItemText, Skeleton } from "@mui/material";
import { createNotification, icon, IconComponent } from "../../../../common/components";
import { translate } from "../../../../common/providers";
import { ApiService } from "../../../../api";
import {
	setSelectedTheme,
	setSelectedThemeUuid,
	setOpenDrawer,
	fetchCommitments,
	setSelectedCommitment,
	setPage,
	setManualSelectionThemeUuuid,
	updateThemeList,
} from "../../slice/commitmentTrackingSlice";
import { ThemeDrawerOption } from "./components";
import styles from "./ThemeList.module.css";
import { buildFilterPayload } from "../../utils";
import CommitmentSidePanel from "../commitment-sidepanel";

export default function ThemeList({ open }) {
	const [allThemesSelected, setAllThemesSelected] = useState(false);
	const [dialogOpen, setDialogOpen] = useState(false);
	const themes =
		useSelector(
			({ commitmentTracking }) =>
				commitmentTracking.filterDefinitions.find((filterDefinition) => filterDefinition.fieldName === "theme")
					?.possibleValues
		) || [];
	const projectId = useSelector(({ context }) => context?.project?.id);
	const launchProgress = useSelector(({ commitmentTracking }) => commitmentTracking.progress);
	const selectedTheme = useSelector(({ commitmentTracking }) => commitmentTracking.selectedTheme);
	const themeFieldUuid = useSelector(({ commitmentTracking }) => commitmentTracking.themeFieldUuid);
	const filterDefinitions = useSelector(({ commitmentTracking }) => commitmentTracking.filterDefinitions);
	const appliedFilters = useSelector(({ commitmentTracking }) => commitmentTracking.appliedFilters);
	const selectedThemeUuid = useSelector(({ commitmentTracking }) => commitmentTracking.selectedThemeUuid);
	const limit = useSelector(({ commitmentTracking }) => commitmentTracking.limit);
	const manualSelectionThemeUuuid = useSelector(
		({ commitmentTracking }) => commitmentTracking.manualSelectionThemeUuuid
	);
	const validThemes = themes.filter((theme) => {
		const commitmentCount = typeof theme === "object" ? theme.commitmentCount : 0;
		return commitmentCount > 0;
	});
	const totalCount = useMemo(() => validThemes.reduce((acc, curr) => acc + curr.commitmentCount, 0), [validThemes]);
	const hasThemes = validThemes.length > 0;
	const dispatch = useDispatch();
	const cancelTokenSourceRef = useRef(null);

	useEffect(() => {
		cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		return () => {
			ApiService.cancelTokens(cancelTokenSourceRef.current);
		};
	}, []);

	useEffect(() => {
		if (
			!selectedThemeUuid &&
			selectedTheme === translate("commitment-tracking.theme-list.all") &&
			!allThemesSelected
		) {
			setAllThemesSelected(true);
		}
	}, [allThemesSelected, selectedTheme, selectedThemeUuid]);

	const handleThemeClick = useCallback(
		(theme) => {
			if (theme) {
				setAllThemesSelected(false);
			}
			const cancelToken = cancelTokenSourceRef.current?.token;
			const payload = buildFilterPayload(filterDefinitions, appliedFilters);
			const themeFieldName = "theme";
			if (theme) {
				const themeName = typeof theme === "string" ? theme : theme.value;
				const themeUuid = typeof theme === "object" ? theme.valueUuid : theme;
				dispatch(setSelectedTheme(themeName));
				dispatch(setSelectedThemeUuid(themeUuid));
				dispatch(setSelectedCommitment(null));
				dispatch(setPage(0));
				if (themeFieldUuid) {
					payload[themeFieldName] = {
						is: true,
						matchAll: true,
						elements: [themeUuid],
					};
					dispatch(
						fetchCommitments({
							projectId,
							payload,
							page: 0,
							limit: 25,
							cancelToken,
						})
					);
				}
			} else {
				dispatch(setSelectedTheme(translate("commitment-tracking.theme-list.all")));
				dispatch(setSelectedThemeUuid(null));
				dispatch(setSelectedCommitment(null));
				dispatch(setPage(0));
				payload[themeFieldName] = {
					is: true,
					matchAll: true,
					elements: [],
				};
				dispatch(fetchCommitments({ projectId, payload, page: 0, limit: 25, cancelToken }));
			}
		},
		[dispatch, projectId, themeFieldUuid, filterDefinitions, appliedFilters]
	);
	const handleToggleDrawer = useCallback(() => {
		dispatch(setOpenDrawer(!open));
	}, [dispatch, open]);

	const handleSelectAll = () => {
		if (!launchProgress) {
			setAllThemesSelected(true);
			handleThemeClick(null);
		}
	};
	const buildPayloadFromFilters = useCallback(
		(filters) => buildFilterPayload(filterDefinitions, filters),
		[filterDefinitions]
	);
	const handleCreateCommitment = async () => {
		try {
			const cancelToken = cancelTokenSourceRef.current?.token;
			await dispatch(
				updateThemeList({
					projectId,
					filters: appliedFilters,
					themeFieldUuid,
					cancelToken,
				})
			).unwrap();
			dispatch(setPage(0));
			const payload = buildPayloadFromFilters(appliedFilters, true);
			if (selectedThemeUuid && themeFieldUuid) {
				const themeField = filterDefinitions.find((field) => field.fieldUuid === themeFieldUuid);
				const themeFieldName = themeField ? themeField.fieldName.toLowerCase().replace(/ /g, "_") : "theme";
				payload[themeFieldName] = {
					is: true,
					matchAll: true,
					elements: [selectedThemeUuid],
				};
			}
			await dispatch(
				fetchCommitments({
					projectId,
					payload,
					page: 0,
					limit,
					cancelToken,
				})
			).unwrap();
		} catch (error) {
			console.error("Error refreshing data after commitment creation:", error);
			createNotification({
				type: "error",
				message: translate("commitment-tracking.notifications.commitment-created-error"),
			});
		}
	};
	const handleNewCommitment = () => {
		setDialogOpen(true);
	};
	const handleDialogClose = () => {
		setDialogOpen(false);
	};
	useEffect(() => {
		if (manualSelectionThemeUuuid) {
			const redirectedTheme = validThemes.find((theme) => theme.valueUuid === manualSelectionThemeUuuid);
			if (redirectedTheme) {
				handleThemeClick(redirectedTheme);
				dispatch(setManualSelectionThemeUuuid(null));
			}
		}
	}, [manualSelectionThemeUuuid, validThemes, dispatch, handleThemeClick]);
	return (
		<div className={styles.wrapper}>
			<ThemeDrawerOption open={open} onNewCommitment={handleNewCommitment} onToggle={handleToggleDrawer} />
			{open && (
				<>
					{hasThemes ? (
						<>
							<div className={styles.title}>{translate("commitment-tracking.theme-list.title-text")}</div>
							<div
								className={styles.subTitle}
								data-progress-ongoing={!!launchProgress}
								data-selected={allThemesSelected}
								role="presentation"
								onClick={handleSelectAll}
							>
								<div>{translate("commitment-tracking.theme-list.all")}</div>
								<div>{totalCount}</div>
							</div>
							<div className={styles.scrollcontainer}>
								<List className={styles.list}>
									{validThemes.map((theme) => {
										const themeName = typeof theme === "string" ? theme : theme.value;
										const commitmentCount = typeof theme === "object" ? theme.commitmentCount : 0;
										if (!launchProgress) {
											return (
												<ListItem key={themeName} disablePadding>
													<ListItemButton
														className={styles.listItemButton}
														selected={selectedTheme === themeName}
														sx={{
															"&.Mui-selected": {
																backgroundColor: "var(--grey-100)",
															},
															"&.Mui-selected:hover": {
																backgroundColor: "var(--grey-100)",
															},
														}}
														onClick={() => handleThemeClick(theme)}
													>
														<ListItemText
															primary={themeName}
															primaryTypographyProps={{
																className: styles.listItemText,
															}}
															sx={{ margin: 0 }}
														/>
														<span className={styles.commitmentCount}>
															{commitmentCount}
														</span>
													</ListItemButton>
												</ListItem>
											);
										}
										return (
											<ListItem key={themeName} disablePadding>
												<ListItemButton disabled className={styles.listItemButton}>
													<Skeleton
														height="var(--spacing-s)"
														variant="rectangular"
														width="100%"
													/>
												</ListItemButton>
											</ListItem>
										);
									})}
								</List>
							</div>
						</>
					) : (
						<div className={styles.emptyState}>
							{!launchProgress && (
								<>
									<IconComponent color="var(--blue-500) !important" icon={icon.faInfoCircle} />
									<div className={styles.emptyStateText}>
										{translate("commitment-tracking.empty-state.no-results")}
									</div>
								</>
							)}
						</div>
					)}
				</>
			)}
			<CommitmentSidePanel
				appliedFilters={appliedFilters}
				filterDefinitions={filterDefinitions}
				mode="create"
				open={dialogOpen}
				projectId={projectId}
				selectedTheme={selectedTheme}
				themeFieldUuid={themeFieldUuid}
				onClose={handleDialogClose}
				onSubmit={handleCreateCommitment}
			/>
		</div>
	);
}

ThemeList.propTypes = {
	open: PropTypes.bool,
};

ThemeList.defaultProps = {
	open: true,
};
